import { vars } from '@styles/vanilla-extract/theme.css';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import React, { ReactNode } from 'react';

import round, { roundFloat } from '@utils/round';

import {
	barClass,
	barHeightRate,
	containerClass,
	innerClass,
	labelClass,
	strokeColor,
	valueClass,
	valueContainerClass,
	valueStokeClass,
	valueUnitClass,
} from './style.css';
import ColoredChartText from '../colored-chart-text/ColoredChartText';

export interface VerticalBarAverageChartProps {
	desc?: ReactNode;
	subDesc?: string;
	isLoading?: boolean;
	isError?: boolean;
	lackOfPermission?: boolean;
	barChartValues?: number[];
	label?: string;
	isPositive?: boolean;
	value?: number;
	valueUnit?: string;
	isFloat?: boolean;
}

const VerticalBarAverageChart = ({
	desc,
	subDesc,
	isLoading,
	isError,
	lackOfPermission,
	barChartValues = [],
	label,
	isPositive,
	value,
	valueUnit,
	isFloat,
}: VerticalBarAverageChartProps) => {
	/**
	 * 차트 값 배열 중 가장 큰 값을 기준으로 비율을 계산합니다.
	 */
	const getRate = (value: number) => value / Math.max(...barChartValues);
	/**
	 * 숫자 배열의 평균값을 반환합니다.
	 */
	const getAverage = (values: number[]) =>
		values.reduce((acc, cur) => acc + cur, 0) / values.length;
	/**
	 * 텍스트가 포함된 문자열에서 소수점을 포함한 숫자를 추출합니다.
	 */
	const getNumberFromValue = (value: string) => value.replace(/[^\d.,]/g, '');
	/**
	 * 텍스트가 포함된 문자열에서 숫자 및 소수점을 제외한 문자열을 추출합니다.
	 */
	const getStringFromValue = (value: string) => value.replace(/[\d.,]/g, '');
	const average = value ?? 0;
	const roundedValue = round(average, 'down') ?? '';
	const roundedFloatValue = roundFloat(average, 'down', 1) ?? '';

	const available = !isLoading && !isError && !lackOfPermission;
	barChartValues = barChartValues ?? [119, 83, 107, 115, 57, 97, 83];

	const getStrokeColor = () =>
		lackOfPermission
			? vars.global.colors.primary[60]
			: isLoading || isError
			? vars.global.colors.secondary[60]
			: isPositive
			? vars.global.colors.primary[60]
			: vars.global.colors.gray[80];

	return (
		<div className={containerClass}>
			{desc && (
				<ColoredChartText
					subText={subDesc}
					isLoading={isLoading}
					isError={isError}
					lackOfPermission={lackOfPermission}
				>
					{desc}
				</ColoredChartText>
			)}

			<div className={innerClass}>
				{barChartValues?.map((value, idx) => {
					return (
						<div
							key={idx}
							className={barClass}
							style={assignInlineVars({ [barHeightRate]: getRate(value).toString() })}
						/>
					);
				})}

				<div
					className={valueStokeClass}
					style={assignInlineVars({
						[barHeightRate]: '0.6',
						[strokeColor]: getStrokeColor(),
					})}
				>
					<div className={labelClass}>{label}</div>
					<div className={valueContainerClass}>
						<div className={valueClass}>
							{available
								? value?.toString()
									? isFloat
										? roundedFloatValue
										: getNumberFromValue(roundedValue)
									: '-'
								: '?'}
						</div>
						<div className={valueUnitClass}>{`${
							isFloat ? '' : getStringFromValue(roundedValue)
						} ${valueUnit ?? ''}`}</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default VerticalBarAverageChart;
